import { useEffect, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";
import Image from "next/image";
import { useRouter } from "next/router";

/** MUI */
import { Alert, Container, Grid, NoSsr, useMediaQuery, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import { getFullTopMenu, setSkipLinksPage } from "@/src/_appStore/Common";

/** API */
import { getCatalogValuesFromAPI } from "@/src/_APIs";

/** Helpers */
import { useTranslation } from "react-i18next";
import useHelper from "@/src/_common/useHelper";

/** matomo */
// import { useMatomo } from "@datapunt/matomo-tracker-react";

/** UI */
const Certificate = dynamic(() => import("../_Commons/Certificate/Certificate"));
const ContentIsUnderConstruction = dynamic(() => import("@/src/_common/ContentIsUnderConstruction"));
const Preloader = dynamic(() => import("@/src/_common/Preloader"));
const ValuesAccordion = dynamic(() => import("./componentsVHD/ValuesAccordion"));

const useStyles = makeStyles((theme) => ({
    root: {
        paddingBottom: 1,
    },
    boxedWrapper: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(0, 3),
        margin: theme.spacing(3, 0),
        "& + &": {
            marginTop: theme.spacing(3.5),
        },
    },
    ContentHolder: {
        position: "relative",
        padding: theme.spacing(0, 3, 0, 3), //0
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(0, 2, 0, 6),
        },
    },
    titles: {
        fontSize: "1.77778rem",
        fontWeight: 600,
        marginBottom: 14, //theme.spacing(4),
        color: theme.palette.primary.main,
        "&:focus-visible": {
            ...theme.outliness,
        },
    },
    intro: {
        // margin: theme.spacing(5, 0),
        margin: theme.spacing(2.5, 0, 3, 0),
        // textAlign:'justify',
        // textWrap:'balance',
        "& p": {
            margin: 0,
        },
    },
}));

const ValuesIndex = () => {
    const { t, i18n } = useTranslation();
    const { translationFromJson } = useHelper();
    const classes = useStyles();
    const dispatch = useDispatch();
    // const { trackPageView } = useMatomo();
    const router = useRouter();

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    /** states */
    const [LOADING, setLOADING] = useState(false);
    const [ERROR, setERROR] = useState(false);
    const [DATA, setDATA] = useState(null);
    const [HVD_ID, setHVD_ID] = useState(null);
    const [I_ID, setI_ID] = useState(null);
    const [S_ID, setS_ID] = useState(null);
    const [OPEN_CERTIFICATE, setOPEN_CERTIFICATE] = useState(false);
    /** - redux */
    const fullTopMenu = useSelector(getFullTopMenu);

    /** Effects */
    /** - get API data */
    useEffect(() => {
        setLOADING(true);
        setERROR(false);
        getCatalogValuesFromAPI({ lang: i18n.language })
            .then((response) => {
                if (response.error || response.message?.type === "Error") {
                    if (router?.query?.hasOwnProperty("sample") && response?.sample) {
                        setDATA(response.sample);
                        return;
                    }
                    setERROR(response);
                } else {
                    setDATA(response);
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setLOADING(false);
            });
    }, [i18n.language]);

    useEffect(() => {
        setHVD_ID(router?.query?.o);
        setI_ID(router?.query?.i);
        setS_ID(router?.query?.s);
    }, [router]);

    useEffect(() => {
        // const desc = `${t("Dane o wysokiej wartości")}. ${DATA?.intro?.length > 1 ? DATA?.intro : ""}`;

        dispatch(setSkipLinksPage({ id: "hvd" }));
        return () => dispatch(setSkipLinksPage(""));
    }, [DATA]);

    /** Handlers */
    const handleToggleCertificate = ({ show }) => {
        setOPEN_CERTIFICATE(show);
    };

    return (
        <div className={classes.root}>
            <NoSsr>
                <Container maxWidth={false} className={classes.ContentHolder}>
                    {/* <pre style={{ fontSize: 10 }}>{JSON.stringify(DATA, null, 4)}</pre> */}

                    <Grid container spacing={0} sx={{ position: "relative" }}>
                        <Grid item xs={12}>
                            <Typography className={classes.titles} id={"hvd-main-title"} tabIndex={-1} component={"h2"}>
                                {t("Dane o wysokiej wartości")}
                                <Image
                                    src={"/images/HVD_icon.svg"}
                                    alt=""
                                    width={54}
                                    height={24}
                                    style={{ marginLeft: 8 }}
                                />
                            </Typography>
                        </Grid>

                        {!!ERROR && (
                            <Container maxWidth={"xl"}>
                                <Alert severity="error" variant={"outlined"}>
                                    {translationFromJson(ERROR.message, "text")}
                                </Alert>
                            </Container>
                        )}

                        {LOADING ? (
                            <Grid item xs={12}>
                                <Preloader type={"linear"} />
                            </Grid>
                        ) : null}

                        <>
                            {!!DATA?.intro && (
                                <Grid item xs={12} className={classes.boxedWrapper}>
                                    <Typography
                                        variant={"body1"}
                                        className={classes.intro}
                                        dangerouslySetInnerHTML={{
                                            __html: translationFromJson(DATA, "intro"),
                                        }}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} className={classes.boxedWrapper}>
                                {!!DATA?.data?.length && (
                                    <ValuesAccordion
                                        items={DATA.data}
                                        type={"OTHER"}
                                        tip={{ open: t("Rozwiń"), close: t("Zwiń") }}
                                        label={{
                                            open: t("Rozwiń, aby zobaczyć pełną treść"),
                                            close: t("Zwiń"),
                                        }}
                                        hvd_id={HVD_ID}
                                        i_id={I_ID}
                                        s_id={S_ID}
                                        onToggleCertificate={(show) => handleToggleCertificate({ show })}
                                        // type={"DB"}
                                    />
                                )}
                            </Grid>
                        </>
                    </Grid>
                </Container>

                <Certificate
                    indicatorId={OPEN_CERTIFICATE}
                    // item={item}
                    show={OPEN_CERTIFICATE}
                    onClose={(show) => handleToggleCertificate({ show })}
                    color={"primary"}
                    from={"hvd"}
                    hideIcon={true}
                />
            </NoSsr>
        </div>
    );
};

export default ValuesIndex;
